/**
 * Component: DateRangePicker
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R07
 *
 * Date range picker for filtering usage dashboard data.
 * Accessibility: WCAG 2.1 AA compliant with keyboard navigation
 */

import React from 'react';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface DateRangePickerProps {
  /** Current date range selection */
  dateRange: DateRange | undefined;
  /** Callback when date range changes */
  onDateRangeChange: (range: DateRange | undefined) => void;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
}

/** Quick select presets for common date ranges */
type PresetKey = '7d' | '30d' | '90d' | 'this_month' | 'last_month' | 'custom';

interface Preset {
  label: string;
  value: PresetKey;
}

const presets: Preset[] = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'This month', value: 'this_month' },
  { label: 'Last month', value: 'last_month' },
  { label: 'Custom range', value: 'custom' },
];

/**
 * Get date range from preset
 */
function getPresetDateRange(preset: PresetKey): DateRange | undefined {
  const today = new Date();

  switch (preset) {
    case '7d':
      return { from: subDays(today, 7), to: today };
    case '30d':
      return { from: subDays(today, 30), to: today };
    case '90d':
      return { from: subDays(today, 90), to: today };
    case 'this_month':
      return { from: startOfMonth(today), to: today };
    case 'last_month': {
      const lastMonth = subMonths(today, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    case 'custom':
      return undefined;
    default:
      return undefined;
  }
}

/**
 * DateRangePicker component for selecting date ranges with presets.
 *
 * ADMIN-V1-R07: Supports filtering usage data by time period.
 */
export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  disabled = false,
  className,
}: DateRangePickerProps): React.ReactElement {
  const [selectedPreset, setSelectedPreset] = React.useState<PresetKey>('30d');

  /**
   * Handle preset selection
   */
  const handlePresetChange = (value: PresetKey): void => {
    setSelectedPreset(value);
    if (value !== 'custom') {
      const range = getPresetDateRange(value);
      onDateRangeChange(range);
    }
  };

  /**
   * Handle calendar selection
   */
  const handleCalendarSelect = (range: DateRange | undefined): void => {
    setSelectedPreset('custom');
    onDateRangeChange(range);
  };

  /**
   * Format the display string for the date range
   */
  const formatDateRangeDisplay = (): string => {
    if (!dateRange?.from) {
      return 'Select date range';
    }

    if (!dateRange.to) {
      return format(dateRange.from, 'MMM d, yyyy');
    }

    return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
  };

  return (
    <div className={cn('flex flex-col gap-2 sm:flex-row sm:items-center', className)}>
      {/* Preset selector */}
      <Select
        value={selectedPreset}
        onValueChange={(value: PresetKey) => handlePresetChange(value)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full sm:w-40" aria-label="Select time period">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          {presets.map((preset) => (
            <SelectItem key={preset.value} value={preset.value}>
              {preset.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Date range display and calendar popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full justify-start text-left font-normal sm:w-auto sm:min-w-[260px]',
              !dateRange && 'text-muted-foreground'
            )}
            disabled={disabled}
            aria-label={`Selected date range: ${formatDateRangeDisplay()}`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>{formatDateRangeDisplay()}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleCalendarSelect}
            numberOfMonths={2}
            disabled={{ after: new Date() }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DateRangePicker;
